import { useMemo, useState } from 'react';
import { subDays, format } from 'date-fns';
import { Box, VStack, SimpleGrid } from '@chakra-ui/react';
import ConversationChart from './dashboard-conversation-chart';
import Filters from './dashboard-filters';
import { DashboardWrapper } from './dashboard-wrapper';
import SentimentChart from './dashboard-sentiment-chart';
import StatsCard from './dashboard-stats-card';
import InteractionsChart from './dashboard-interactions-chart';
import { FiMessageCircle, FiMail, FiUsers } from 'react-icons/fi';
import {
  useDashboardStats,
  useInteractionsUsage,
  useSentimentAnalysis,
} from '../dashboard.hook';
import { DashboardFilters } from '../dashboard.types';
import { persistStorageService } from '@/features/auth/persist-storage';

interface FilterState {
  startDate: string;
  endDate: string;
  platform: string;
}

export default function DashboardPage() {
  const bgColor = 'white'; // Static background color

  // Get today's date and last week's date (today - 7 days) in YYYY-MM-DD format
  const todayDate = new Date();
  const today = format(todayDate, 'yyyy-MM-dd');
  const lastWeek = format(subDays(todayDate, 7), 'yyyy-MM-dd');

  const [filters, setFilters] = useState<FilterState>({
    startDate: lastWeek,
    endDate: today,
    platform: 'ALL',
  });

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  // Build API filters using the selected date range
  const apiFilters: DashboardFilters = useMemo(() => {
    const medium =
      filters.platform === 'WhatsApp'
        ? 'whatsapp'
        : filters.platform === 'Web'
          ? 'web'
          : undefined;

    return {
      start_date: filters.startDate,
      end_date: filters.endDate,
      medium,
    };
  }, [filters]);

  const { data: stats, isLoading: isLoadingStats } = useDashboardStats(
    apiFilters,
    true,
  );

  const { data: sentimentData, isLoading: isLoadingSentiment } =
    useSentimentAnalysis(apiFilters, true);

  const selectedLocation = persistStorageService.getPersistItem<{
    id: string;
  } | null>({
    name: 'selectedLocation',
    parse: true,
  });

  const interactionsEnabled = Boolean(selectedLocation?.id);

  const { data: interactions, isLoading: isLoadingInteractions } =
    useInteractionsUsage(apiFilters, interactionsEnabled);

  return (
    <DashboardWrapper>
      <Box bg={bgColor} minH="100vh" p={6}>
        <VStack gap={6} align="stretch" w="100%">
          {/* Filters */}
          <Box>
            <Filters
              startDate={filters.startDate}
              endDate={filters.endDate}
              platform={filters.platform}
              onFilterChange={handleFilterChange}
            />
          </Box>

          <SimpleGrid columns={{ base: 3, md: 3 }} gap={6}>
            <StatsCard
              title="Total Conversations"
              value={stats?.totalConversations ?? 0}
              isLoading={isLoadingStats}
              icon={FiMessageCircle}
              iconColor="blue.500"
            />
            <StatsCard
              title="Total Messages"
              value={stats?.totalMessages ?? 0}
              isLoading={isLoadingStats}
              icon={FiMail}
              iconColor="green.500"
            />
            <StatsCard
              title="Total Users"
              value={stats?.totalUsers ?? 0}
              isLoading={isLoadingStats}
              icon={FiUsers}
              iconColor="purple.500"
            />
          </SimpleGrid>

          {/* Dashboard Grid */}
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
            <SentimentChart
              sentimentData={{
                positive: sentimentData?.positive ?? 0,
                neutral: sentimentData?.neutral ?? 0,
                negative: sentimentData?.negative ?? 0,
              }}
              isLoading={isLoadingSentiment}
            />
            <InteractionsChart
              used={interactions?.used ?? 0}
              limit={
                interactions?.limit ?? Math.max(interactions?.used ?? 0, 1)
              }
              isLoading={isLoadingInteractions}
            />
            {/* <TokensChart
              tokens={15000}
              limit={25000}
              isLoading={isLoadingStats}
              /> */}
          </SimpleGrid>
          <ConversationChart
            startDate={filters.startDate}
            endDate={filters.endDate}
            platform={filters.platform}
          />
        </VStack>
      </Box>
    </DashboardWrapper>
  );
}
